// Medical Scores Database - Extended Cardiology and Thrombosis Scores
// Comprehensive cardiovascular and thromboembolic risk assessment

import {
  Heart,
  Droplets,
  Activity,
  AlertTriangle
} from 'lucide-react';

// ==================== CARDIOLOGIE ÉTENDUE SUPPLÉMENTAIRE ====================
export const CARDIOLOGY_EXTRA_SCORES = {
  chads2vasc: {
    name: 'CHA₂DS₂-VASc',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque embolique sur FA (version actuelle)',
    color: 'red',
    fields: [
      { id: 'chf', label: 'Insuffisance cardiaque / FEVG ≤ 40%', type: 'checkbox', points: 1 },
      { id: 'htn', label: 'HTA', type: 'checkbox', points: 1 },
      { id: 'age75', label: 'Âge ≥ 75 ans (2 points)', type: 'checkbox', points: 2 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 1 },
      { id: 'stroke', label: 'AVC/AIT/embolie (2 points)', type: 'checkbox', points: 2 },
      { id: 'vascular', label: 'Maladie vasculaire (IDM, AOMI, plaque aortique)', type: 'checkbox', points: 1 },
      { id: 'age65', label: 'Âge 65-74 ans', type: 'checkbox', points: 1 },
      { id: 'female', label: 'Sexe féminin', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.chf) total += 1;
      if (values.htn) total += 1;
      if (values.age75) total += 2;
      if (values.diabetes) total += 1;
      if (values.stroke) total += 2;
      if (values.vascular) total += 1;
      if (values.age65 && !values.age75) total += 1;
      if (values.female) total += 1;
      return { score: total, max: 9 };
    },
    interpret: (score) => {
      const risks = ['0%', '1.3%', '2.2%', '3.2%', '4.0%', '6.7%', '9.8%', '9.6%', '6.7%', '15.2%'];
      if (score === 0) return { level: 'success', text: 'CHA₂DS₂-VASc 0', detail: `Risque AVC/an: ${risks[0]}. Homme: pas d'anticoagulation` };
      if (score === 1) return { level: 'warning', text: 'CHA₂DS₂-VASc 1', detail: `Risque: ${risks[1]}. Femme seul facteur: pas d'AC. Sinon: AOD à considérer` };
      return { level: 'danger', text: `CHA₂DS₂-VASc ${score}`, detail: `Risque: ${risks[Math.min(score, 9)]}. Anticoagulation recommandée` };
    },
    formula: 'CHA₂DS₂-VASc = IC + HTA + Âge + Diabète + AVC(×2) + Vasc + Sexe'
  },

  hasbled: {
    name: 'HAS-BLED',
    category: 'Cardiologie',
    icon: Droplets,
    description: 'Risque hémorragique sur FA anticoagulée',
    color: 'red',
    fields: [
      { id: 'hypertension', label: 'HTA (PAS > 160 mmHg)', type: 'checkbox', points: 1 },
      { id: 'renal', label: 'Insuffisance rénale (dialyse, greffe, créat > 200)', type: 'checkbox', points: 1 },
      { id: 'liver', label: 'Insuffisance hépatique (cirrhose, bili×2, transam×3)', type: 'checkbox', points: 1 },
      { id: 'stroke', label: 'ATCD AVC', type: 'checkbox', points: 1 },
      { id: 'bleeding', label: 'ATCD hémorragie ou prédisposition', type: 'checkbox', points: 1 },
      { id: 'inr', label: 'INR labile (< 60% dans la cible)', type: 'checkbox', points: 1 },
      { id: 'elderly', label: 'Âge > 65 ans', type: 'checkbox', points: 1 },
      { id: 'drugs', label: 'Médicaments (AINS, antiplaquettaires)', type: 'checkbox', points: 1 },
      { id: 'alcohol', label: 'Alcool (≥ 8 verres/semaine)', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 9 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `HAS-BLED ${score}`, detail: 'Risque hémorragique faible. Anticoagulation recommandée' };
      return { level: 'warning', text: `HAS-BLED ${score}`, detail: 'Risque hémorragique élevé. Ne contre-indique pas l\'anticoagulation mais surveillance' };
    },
    formula: 'HAS-BLED: H=HTA, A=Anormal rein/foie, S=Stroke, B=Bleeding, L=Labile INR, E=Elderly, D=Drugs/alcohol'
  },

  wellsEP: {
    name: 'Wells Embolie Pulmonaire',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Probabilité embolie pulmonaire',
    color: 'red',
    fields: [
      { id: 'dvtSymptoms', label: 'Signes cliniques TVP', type: 'checkbox', points: 3 },
      { id: 'alternative', label: 'Diagnostic alternatif moins probable que EP', type: 'checkbox', points: 3 },
      { id: 'tachycardia', label: 'FC > 100/min', type: 'checkbox', points: 1.5 },
      { id: 'immobilization', label: 'Immobilisation ≥ 3j ou chirurgie < 4 sem', type: 'checkbox', points: 1.5 },
      { id: 'history', label: 'ATCD TVP/EP', type: 'checkbox', points: 1.5 },
      { id: 'hemoptysis', label: 'Hémoptysie', type: 'checkbox', points: 1 },
      { id: 'cancer', label: 'Cancer actif ou < 6 mois', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.dvtSymptoms) total += 3;
      if (values.alternative) total += 3;
      if (values.tachycardia) total += 1.5;
      if (values.immobilization) total += 1.5;
      if (values.history) total += 1.5;
      if (values.hemoptysis) total += 1;
      if (values.cancer) total += 1;
      return { score: total, max: 12.5 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `Wells EP ${score}`, detail: 'Probabilité faible (8-10%). D-Dimères, si neg: EP exclue' };
      if (score <= 6) return { level: 'warning', text: `Wells EP ${score}`, detail: 'Probabilité modérée (28%). D-Dimères puis angio-TDM' };
      return { level: 'danger', text: `Wells EP ${score}`, detail: 'Probabilité forte (78%). Angio-TDM directement' };
    },
    formula: 'Wells EP: version originale avec seuils 4 et 6'
  },

  score2: {
    name: 'SCORE2 / SCORE2-OP',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque CV à 10 ans (ESC 2021)',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 40, max: 89 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' }, { value: 'F', label: 'Femme' }
      ]},
      { id: 'smoker', label: 'Fumeur actuel', type: 'checkbox', points: 0 },
      { id: 'sbp', label: 'PAS (mmHg)', type: 'number', min: 100, max: 200 },
      { id: 'cholTotal', label: 'Cholestérol total (mmol/L)', type: 'number', min: 3, max: 10, step: 0.1 },
      { id: 'hdl', label: 'HDL (mmol/L)', type: 'number', min: 0.5, max: 3, step: 0.1 },
      { id: 'region', label: 'Région de risque (France = faible)', type: 'select', options: [
        { value: 'low', label: 'Risque faible (France, Espagne, Italie...)' },
        { value: 'moderate', label: 'Risque modéré (Allemagne, Belgique...)' },
        { value: 'high', label: 'Risque élevé (Europe Est)' },
        { value: 'vhigh', label: 'Risque très élevé' }
      ]}
    ],
    calculate: (values) => {
      // Simplified SCORE2 estimation
      const age = values.age || 50;
      const isMale = values.sex === 'M';
      const smoker = values.smoker;
      const sbp = values.sbp || 130;
      const chol = values.cholTotal || 5;
      const hdl = values.hdl || 1.3;
      
      let baseRisk = isMale ? 3 : 1.5;
      baseRisk *= (1 + (age - 50) * 0.05);
      if (smoker) baseRisk *= 2;
      baseRisk *= (1 + (sbp - 120) * 0.01);
      baseRisk *= (1 + (chol - 5) * 0.2);
      baseRisk *= (1 - (hdl - 1.3) * 0.3);
      
      return { score: Math.round(baseRisk * 10) / 10, max: '%' };
    },
    interpret: (score) => {
      if (score < 2.5) return { level: 'success', text: `SCORE2: ${score}%`, detail: 'Risque faible < 2.5%. Conseils hygiéno-diététiques' };
      if (score < 7.5) return { level: 'warning', text: `SCORE2: ${score}%`, detail: 'Risque modéré 2.5-7.5%. Traitement selon comorbidités' };
      return { level: 'danger', text: `SCORE2: ${score}%`, detail: 'Risque élevé ≥ 7.5%. Traitement médicamenteux recommandé' };
    },
    formula: 'SCORE2 2021: remplace SCORE classique. Seuils selon âge et région'
  },

  dapt: {
    name: 'Score DAPT',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Durée bithérapie antiplaquettaire post-stent',
    color: 'red',
    fields: [
      { id: 'age75', label: 'Âge ≥ 75 ans', type: 'select', options: [
        { value: -2, label: '≥ 75 ans (-2 pts)' }, { value: -1, label: '65-74 ans (-1 pt)' }, { value: 0, label: '< 65 ans (0 pt)' }
      ]},
      { id: 'smoker', label: 'Fumeur actuel', type: 'checkbox', points: 1 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 1 },
      { id: 'mi', label: 'IDM inaugural', type: 'checkbox', points: 1 },
      { id: 'priorPCI', label: 'ATCD angioplastie ou IDM', type: 'checkbox', points: 1 },
      { id: 'paclitaxel', label: 'Stent au paclitaxel', type: 'checkbox', points: 1 },
      { id: 'diameter', label: 'Diamètre stent < 3 mm', type: 'checkbox', points: 1 },
      { id: 'chf', label: 'Insuffisance cardiaque ou FEVG < 30%', type: 'checkbox', points: 2 },
      { id: 'graft', label: 'Stent sur greffon veineux', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = values.age75 || 0;
      if (values.smoker) total += 1;
      if (values.diabetes) total += 1;
      if (values.mi) total += 1;
      if (values.priorPCI) total += 1;
      if (values.paclitaxel) total += 1;
      if (values.diameter) total += 1;
      if (values.chf) total += 2;
      if (values.graft) total += 2;
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `DAPT ${score}`, detail: 'DAPT 6 mois recommandée puis monothérapie' };
      return { level: 'warning', text: `DAPT ${score}`, detail: 'DAPT prolongée (> 12 mois) à considérer' };
    },
    formula: 'DAPT ≥ 2 = prolongation bithérapie bénéfique'
  },

  preciseDapt: {
    name: 'PRECISE-DAPT',
    category: 'Cardiologie',
    icon: Droplets,
    description: 'Risque hémorragique sous DAPT',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 100 },
      { id: 'clcr', label: 'Clairance créatinine (mL/min)', type: 'number', min: 10, max: 150 },
      { id: 'hb', label: 'Hémoglobine (g/dL)', type: 'number', min: 5, max: 20, step: 0.1 },
      { id: 'wbc', label: 'Leucocytes (×10⁹/L)', type: 'number', min: 1, max: 30, step: 0.1 },
      { id: 'bleeding', label: 'ATCD hémorragie spontanée', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      const age = values.age || 65;
      const clcr = values.clcr || 80;
      const hb = values.hb || 13;
      const wbc = values.wbc || 8;
      const bleeding = values.bleeding ? 1 : 0;
      
      // Simplified formula
      const score = 0.26 * age - 0.12 * clcr - 0.65 * hb + 0.12 * wbc + 6.4 * bleeding + 20;
      return { score: Math.round(score), max: 100 };
    },
    interpret: (score) => {
      if (score < 17) return { level: 'success', text: `PRECISE-DAPT ${score}`, detail: 'Risque hémorragique faible. DAPT prolongée possible' };
      if (score < 25) return { level: 'warning', text: `PRECISE-DAPT ${score}`, detail: 'Risque modéré. DAPT standard (6-12 mois)' };
      return { level: 'danger', text: `PRECISE-DAPT ${score}`, detail: 'Risque élevé. Raccourcir DAPT à 3-6 mois' };
    },
    formula: 'PRECISE-DAPT: Âge, Créatinine, Hb, GB, ATCD hémorragie'
  },

  villalta: {
    name: 'Score de Villalta',
    category: 'Cardiologie',
    icon: Droplets,
    description: 'Syndrome post-thrombotique',
    color: 'red',
    fields: [
      { id: 'heaviness', label: 'Lourdeur', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Légère' }, { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'pain', label: 'Douleur', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'cramps', label: 'Crampes', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'pruritus', label: 'Prurit', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'paresthesia', label: 'Paresthésies', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'telangiectasia', label: 'Télangiectasies', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'edema', label: 'Œdème', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'skinInduration', label: 'Induration cutanée', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'hyperpigmentation', label: 'Hyperpigmentation', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'redness', label: 'Rougeur', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'painCalf', label: 'Douleur à la compression du mollet', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'ulcer', label: 'Ulcère veineux', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      if (values.ulcer) return { score: 'Sévère', max: 33, details: { hasUlcer: true } };
      let total = 0;
      Object.keys(values).forEach(key => {
        if (key !== 'ulcer') total += (values[key] || 0);
      });
      return { score: total, max: 33 };
    },
    interpret: (score, result) => {
      if (result?.details?.hasUlcer) return { level: 'danger', text: 'Villalta: Sévère', detail: 'Ulcère présent = SPT sévère. Compression, soins locaux' };
      if (score < 5) return { level: 'success', text: `Villalta ${score}`, detail: 'Pas de SPT' };
      if (score < 10) return { level: 'warning', text: `Villalta ${score}`, detail: 'SPT léger. Compression classe 2' };
      if (score < 15) return { level: 'warning', text: `Villalta ${score}`, detail: 'SPT modéré. Compression classe 3' };
      return { level: 'danger', text: `Villalta ${score}`, detail: 'SPT sévère. Prise en charge spécialisée' };
    },
    formula: 'Villalta: 5 symptômes + 6 signes (0-3). Ulcère = sévère automatique'
  },

  herdoo2: {
    name: 'Score HERDOO2',
    category: 'Cardiologie',
    icon: Droplets,
    description: 'Arrêt anticoagulation après 1ère TVP chez la femme',
    color: 'red',
    fields: [
      { id: 'hyperpigmentation', label: 'Hyperpigmentation MI', type: 'checkbox', points: 1 },
      { id: 'edema', label: 'Œdème MI', type: 'checkbox', points: 1 },
      { id: 'redness', label: 'Rougeur MI', type: 'checkbox', points: 1 },
      { id: 'dimer', label: 'D-Dimères ≥ 250 µg/L sous anticoagulant', type: 'checkbox', points: 1 },
      { id: 'obesity', label: 'IMC ≥ 30 kg/m²', type: 'checkbox', points: 1 },
      { id: 'age65', label: 'Âge ≥ 65 ans', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `HERDOO2 ${score}`, detail: 'Risque récidive faible (3%/an). Arrêt anticoagulation possible' };
      return { level: 'warning', text: `HERDOO2 ${score}`, detail: 'Risque récidive élevé. Poursuivre anticoagulation' };
    },
    formula: 'HERDOO2 ≤ 1 chez femme après 1ère TVP non provoquée: arrêt AC sûr'
  },

  ichb: {
    name: 'Index Cheville-Bras (IPS)',
    category: 'Cardiologie',
    icon: Activity,
    description: 'Dépistage artériopathie oblitérante',
    color: 'red',
    fields: [
      { id: 'ankleP', label: 'PAS cheville la plus haute (mmHg)', type: 'number', min: 50, max: 300 },
      { id: 'armP', label: 'PAS humérale la plus haute (mmHg)', type: 'number', min: 50, max: 300 }
    ],
    calculate: (values) => {
      const ankle = values.ankleP || 120;
      const arm = values.armP || 130;
      const ips = (ankle / arm).toFixed(2);
      return { score: parseFloat(ips), max: '' };
    },
    interpret: (score) => {
      if (score > 1.3) return { level: 'warning', text: `IPS ${score}`, detail: 'Artères incompressibles (médiacalcose). Mesure non fiable' };
      if (score >= 0.9) return { level: 'success', text: `IPS ${score}`, detail: 'Normal' };
      if (score >= 0.7) return { level: 'warning', text: `IPS ${score}`, detail: 'AOMI légère' };
      if (score >= 0.4) return { level: 'danger', text: `IPS ${score}`, detail: 'AOMI modérée' };
      return { level: 'danger', text: `IPS ${score}`, detail: 'AOMI sévère. Ischémie critique' };
    },
    formula: 'IPS = PAS cheville / PAS humérale. Normal: 0.9-1.3'
  },

  ehra: {
    name: 'Score EHRA',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Retentissement fonctionnel de la FA',
    color: 'red',
    fields: [
      { id: 'class', label: 'Classification EHRA', type: 'select', options: [
        { value: 1, label: 'EHRA I - Aucun symptôme' },
        { value: 2, label: 'EHRA IIa - Symptômes légers, activité normale' },
        { value: 3, label: 'EHRA IIb - Symptômes modérés, activité normale mais gênante' },
        { value: 4, label: 'EHRA III - Symptômes sévères, activité quotidienne affectée' },
        { value: 5, label: 'EHRA IV - Symptômes invalidants, activité impossible' }
      ]}
    ],
    calculate: (values) => ({ score: values.class || 1, max: 5 }),
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'EHRA I', detail: 'FA asymptomatique. Contrôle fréquence suffisant' };
      if (score <= 3) return { level: 'warning', text: `EHRA ${score <= 2 ? 'IIa' : 'IIb'}`, detail: 'Symptômes légers à modérés. Traitement selon préférence' };
      if (score === 4) return { level: 'danger', text: 'EHRA III', detail: 'Symptômes sévères. Contrôle rythme recommandé' };
      return { level: 'danger', text: 'EHRA IV', detail: 'Symptômes invalidants. Contrôle rythme urgent, ablation à discuter' };
    },
    formula: 'EHRA I-IV: évaluation symptômes de FA'
  },

  dasi: {
    name: 'Duke Activity Status Index',
    category: 'Cardiologie',
    icon: Activity,
    description: 'Capacité fonctionnelle cardiaque',
    color: 'red',
    fields: [
      { id: 'selfCare', label: 'Pouvez-vous vous occuper de vous (manger, habiller)?', type: 'checkbox', points: 2.75 },
      { id: 'walking', label: 'Marcher à l\'intérieur ou autour de la maison?', type: 'checkbox', points: 1.75 },
      { id: 'walkBlock', label: 'Marcher un ou deux pâtés de maisons sur terrain plat?', type: 'checkbox', points: 2.75 },
      { id: 'stairs', label: 'Monter un étage ou une côte?', type: 'checkbox', points: 5.50 },
      { id: 'run', label: 'Courir sur une courte distance?', type: 'checkbox', points: 8.00 },
      { id: 'housework', label: 'Faire des travaux ménagers légers?', type: 'checkbox', points: 2.70 },
      { id: 'heavyHousework', label: 'Faire des travaux ménagers lourds?', type: 'checkbox', points: 8.00 },
      { id: 'yardwork', label: 'Faire du jardinage, ratisser des feuilles?', type: 'checkbox', points: 4.50 },
      { id: 'sex', label: 'Avoir des rapports sexuels?', type: 'checkbox', points: 5.25 },
      { id: 'sports', label: 'Participer à des activités récréatives modérées?', type: 'checkbox', points: 6.00 },
      { id: 'strenuous', label: 'Participer à des sports vigoureux?', type: 'checkbox', points: 7.50 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.selfCare) total += 2.75;
      if (values.walking) total += 1.75;
      if (values.walkBlock) total += 2.75;
      if (values.stairs) total += 5.50;
      if (values.run) total += 8.00;
      if (values.housework) total += 2.70;
      if (values.heavyHousework) total += 8.00;
      if (values.yardwork) total += 4.50;
      if (values.sex) total += 5.25;
      if (values.sports) total += 6.00;
      if (values.strenuous) total += 7.50;
      // VO2 estimée = 0.43 × DASI + 9.6
      const vo2 = 0.43 * total + 9.6;
      return { score: total.toFixed(1), max: 58.2, details: { vo2: vo2.toFixed(1) } };
    },
    interpret: (score, result) => {
      const s = parseFloat(score);
      const vo2 = result?.details?.vo2 || 15;
      if (s >= 34) return { level: 'success', text: `DASI ${score}`, detail: `VO2 estimée: ${vo2} mL/kg/min. Bonne capacité fonctionnelle` };
      if (s >= 20) return { level: 'warning', text: `DASI ${score}`, detail: `VO2 estimée: ${vo2} mL/kg/min. Capacité modérée` };
      return { level: 'danger', text: `DASI ${score}`, detail: `VO2 estimée: ${vo2} mL/kg/min. Capacité limitée. Risque chirurgical élevé` };
    },
    formula: 'DASI 0-58.2 points. VO2 = 0.43 × DASI + 9.6'
  }
};

export default CARDIOLOGY_EXTRA_SCORES;
